"use client";

import React from "react";

import { usePathname, useRouter } from "next/navigation";

import { Sparkles } from "lucide-react";
import { Toaster } from "sonner";
import { v4 as uuidv4 } from "uuid";

import { ChatWindow, type ChatDisplayMode } from "@/components/chat/ChatWindow";
import { PoweredByFooter } from "@/components/layout/PoweredByFooter";
import { MainNavbar } from "@/components/navbar/MainNavbar";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { ChatProvider, useChat } from "@/contexts/ChatContext";
import { CollectionsProvider } from "@/contexts/CollectionsContext";
import { DebugProvider } from "@/contexts/DebugContext";
import { OnboardingModalProvider } from "@/contexts/OnboardingModalContext";
import { PreferencesProvider } from "@/contexts/PreferencesContext";
import { SavedBrandsProvider } from "@/contexts/SavedBrandsContext";
import { SavedProductsProvider } from "@/contexts/SavedProductsContext";
import { SavedSearchesProvider } from "@/contexts/SavedSearchesContext";
import { SearchProvider } from "@/contexts/SearchContext";
import { ShoppingCartProvider } from "@/contexts/ShoppingCartContext";
import { SignUpPromptProvider } from "@/contexts/SignUpPromptContext";
import { clearRetiredAnalyticsStorage } from "@/lib/retired-analytics-storage";
import type { BrowseMenuRootSummary } from "@/lib/browse-menu-roots";
import { isLandingPagePath, isSeoLandingPagePath } from "@/lib/landing-page";
import {
  consumeResultsNavigationNewForChat,
  navigateToAgentResults,
  peekResultsNavigationNewForChat,
  SHOW_CHAT_PARAM,
} from "@/lib/results-navigation";
import { colors, tailwindBreakpoints, zIndex } from "@/styles/design-tokens";
import { displayGenderToCategoryToken } from "@/utils/genderMapping";

interface LayoutContentProps {
  children: React.ReactNode;
  browseMenuRoots: BrowseMenuRootSummary[] | null;
}

function LayoutContent({ children, browseMenuRoots }: LayoutContentProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { activeChatId, clearChat, setChatCategory, startNewChat, isStreaming } = useChat();
  const { userId } = useAuth();
  const isHomeLandingPage = isLandingPagePath(pathname);
  const isSeoLandingPage = isSeoLandingPagePath(pathname);

  const isMobileViewport = React.useCallback(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${tailwindBreakpoints.sm - 1}px)`).matches;
  }, []);

  const [chatDisplayMode, setChatDisplayMode] = React.useState<ChatDisplayMode>(() =>
    typeof window !== "undefined" &&
    window.matchMedia(`(max-width: ${tailwindBreakpoints.sm - 1}px)`).matches
      ? "minimized"
      : "floating"
  );

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [openModalCount, setOpenModalCount] = React.useState(0);
  const prevActiveChatIdRef = React.useRef<string | null>(null);
  const userExplicitlyOpenedChatRef = React.useRef(false);
  const pendingNewSearchChatRef = React.useRef<string | null>(null);
  const [hasUnreadMessages, setHasUnreadMessages] = React.useState(false);
  const prevIsStreamingRef = React.useRef(false);
  const [forceShowChat, setForceShowChat] = React.useState(false);
  const forceShowChatLandingOverrideRef = React.useRef(false);

  React.useEffect(() => {
    let savedMode: string | null = null;
    try {
      savedMode = localStorage.getItem("chatDisplayMode");
    } catch {
      /* Public browsing works without persistence. */
    }
    if (isMobileViewport()) {
      setChatDisplayMode("minimized");
      return;
    }

    if (savedMode === "minimized" || savedMode === "floating") setChatDisplayMode(savedMode);
  }, [isMobileViewport]);

  React.useEffect(() => {
    try {
      localStorage.setItem("chatDisplayMode", chatDisplayMode);
    } catch {
      /* Best effort UI preference. */
    }
  }, [chatDisplayMode]);

  React.useEffect(() => {
    if (!pathname.startsWith("/results/")) return;

    if (userExplicitlyOpenedChatRef.current) {
      userExplicitlyOpenedChatRef.current = false;
      return;
    }

    const resultsThreadId = pathname.split("/")[2];
    const params = new URLSearchParams(window.location.search);
    const shouldAutoMaximize = resultsThreadId
      ? peekResultsNavigationNewForChat(resultsThreadId)
      : false;
    const isBrowseMode = params.get("browse") === "true";

    if (shouldAutoMaximize && resultsThreadId) {
      pendingNewSearchChatRef.current = resultsThreadId;
    } else if (
      pendingNewSearchChatRef.current &&
      resultsThreadId &&
      pendingNewSearchChatRef.current !== resultsThreadId
    ) {
      pendingNewSearchChatRef.current = null;
    }

    if (
      pendingNewSearchChatRef.current &&
      pendingNewSearchChatRef.current === resultsThreadId &&
      activeChatId &&
      resultsThreadId
    ) {
      setChatDisplayMode("floating");
      consumeResultsNavigationNewForChat(resultsThreadId);
      pendingNewSearchChatRef.current = null;
    } else if (isBrowseMode && activeChatId) {
      setChatDisplayMode("minimized");
    }
  }, [pathname, activeChatId]);

  React.useEffect(() => {
    const wasStreaming = prevIsStreamingRef.current;
    prevIsStreamingRef.current = isStreaming;
    if (!wasStreaming || isStreaming) return;

    if (chatDisplayMode === "minimized") {
      setHasUnreadMessages(true);
      return;
    }

    if (!isMobileViewport() || !pathname.startsWith("/results/")) return;

    setChatDisplayMode("minimized");
    setHasUnreadMessages(true);
  }, [isStreaming, chatDisplayMode, isMobileViewport, pathname]);

  React.useEffect(() => {
    if (chatDisplayMode !== "minimized") {
      setHasUnreadMessages(false);
    }
  }, [chatDisplayMode]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const showChat = params.get(SHOW_CHAT_PARAM) === "true";

    if (showChat && activeChatId) {
      setChatDisplayMode("floating");
      setForceShowChat(true);
      forceShowChatLandingOverrideRef.current = isHomeLandingPage;

      params.delete(SHOW_CHAT_PARAM);
      const newSearch = params.toString();
      const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [pathname, activeChatId, isHomeLandingPage]);

  React.useEffect(() => {
    if (
      forceShowChat &&
      (chatDisplayMode === "minimized" ||
        (forceShowChatLandingOverrideRef.current && !isHomeLandingPage))
    ) {
      setForceShowChat(false);
      forceShowChatLandingOverrideRef.current = false;
    }
  }, [chatDisplayMode, forceShowChat, isHomeLandingPage]);

  React.useEffect(() => {
    if (
      prevActiveChatIdRef.current &&
      activeChatId &&
      activeChatId !== prevActiveChatIdRef.current
    ) {
      setChatDisplayMode("floating");
    }
    prevActiveChatIdRef.current = activeChatId;
  }, [activeChatId]);

  React.useEffect(() => {
    const handleMobileMenuToggle = (e: Event) => {
      const customEvent = e as CustomEvent<{ isOpen: boolean }>;
      if (customEvent.detail && typeof customEvent.detail.isOpen === "boolean") {
        setIsMobileMenuOpen(customEvent.detail.isOpen);
      }
    };
    window.addEventListener("mobileMenuToggle", handleMobileMenuToggle);
    return () => {
      window.removeEventListener("mobileMenuToggle", handleMobileMenuToggle);
    };
  }, []);

  React.useEffect(() => {
    const handleModalCountDelta = (e: Event) => {
      const customEvent = e as CustomEvent<{ delta: number }>;
      const delta = customEvent.detail?.delta;
      if (typeof delta !== "number" || !Number.isFinite(delta)) return;
      setOpenModalCount((prev) => Math.max(0, prev + delta));
    };
    window.addEventListener("globalModalCountDelta", handleModalCountDelta);
    return () => {
      window.removeEventListener("globalModalCountDelta", handleModalCountDelta);
    };
  }, []);

  React.useEffect(() => {
    const handleMoreLikeThisVisibility = (e: Event) => {
      const customEvent = e as CustomEvent<{ isOpen: boolean }>;
      const isOpen = customEvent.detail?.isOpen;
      if (typeof isOpen !== "boolean") return;
      if (isOpen && !isMobileViewport()) {
        setChatDisplayMode("minimized");
      }
    };
    window.addEventListener("moreLikeThisModalVisibility", handleMoreLikeThisVisibility);
    return () => {
      window.removeEventListener("moreLikeThisModalVisibility", handleMoreLikeThisVisibility);
    };
  }, [isMobileViewport]);

  const navbarVariant = isHomeLandingPage ? "landing" : "default";

  const handleSearchSubmit = async (_searchMode: "new", genderDisplay: string, query: string) => {
    const userInput = query.trim();
    if (!userInput) return;
    const chatId = uuidv4();

    clearChat();

    setChatDisplayMode("floating");

    const categoryToken = displayGenderToCategoryToken(genderDisplay);

    const chatData = {
      query: userInput,
      originalQuery: userInput,
      gender: genderDisplay,
      ...(categoryToken ? { category: categoryToken } : {}),
      userId,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(`chat-${chatId}`, JSON.stringify(chatData));
    setChatCategory(chatId, categoryToken || undefined);

    navigateToAgentResults(router, chatId);
  };

  const isOnResultsPage = pathname.startsWith("/results/");
  const isProductDetailPage = pathname.startsWith("/product/");

  const shouldShowGlobalChat =
    activeChatId &&
    !pathname.startsWith("/chat/") &&
    !pathname.startsWith("/chats") &&
    !pathname.startsWith("/cart") &&
    !pathname.startsWith("/brands") &&
    !pathname.startsWith("/brand-explorer") &&
    // Keep desktop PDPs free of the floating assistant for now so we do not
    // encourage product-specific questions that the chat experience cannot
    // answer reliably yet. We can restore visibility once PDP-specific
    // assistance is supported.
    !(isProductDetailPage && !isMobileViewport()) &&
    !isMobileMenuOpen &&
    (!isHomeLandingPage || forceShowChat) &&
    !(isMobileViewport() && pathname.startsWith("/profile")) &&
    (!isMobileViewport() || isOnResultsPage || isSeoLandingPage || forceShowChat) &&
    (!isMobileViewport() || openModalCount === 0);

  const shouldShowNewChatBubble =
    (isOnResultsPage || isSeoLandingPage) &&
    !activeChatId &&
    !isMobileMenuOpen &&
    (!isMobileViewport() || openModalCount === 0);

  const handleStartNewChat = () => {
    userExplicitlyOpenedChatRef.current = true;
    startNewChat();
    setChatDisplayMode("floating");
  };

  return (
    <>
      <div className="sticky top-0 z-50 bg-white">
        <MainNavbar
          variant={navbarVariant}
          onSearchSubmit={handleSearchSubmit}
          browseMenuRoots={browseMenuRoots}
        />
      </div>
      {children}
      <PoweredByFooter />
      <Toaster position="top-center" style={{ zIndex: zIndex.chatWindow + 1 }} />

      {shouldShowGlobalChat && (
        <ChatWindow
          chatId={activeChatId}
          displayMode={chatDisplayMode}
          onDisplayModeChange={setChatDisplayMode}
          hasUnreadMessages={hasUnreadMessages}
        />
      )}

      {shouldShowNewChatBubble && (
        <button
          onClick={handleStartNewChat}
          className="fixed bottom-4 left-4 right-4 z-50 flex items-center justify-center gap-2 rounded-full px-5 py-3 shadow-lg transition-transform hover:scale-105 sm:left-auto sm:right-6 sm:w-auto"
          style={{ backgroundColor: colors.stroke.pureBlack }}
          title="Open shopping assistant"
        >
          <Sparkles className="h-5 w-5 text-white" />
          <span className="whitespace-nowrap text-sm font-medium text-white">
            Shopping Assistant
          </span>
        </button>
      )}
    </>
  );
}

interface AppShellProps {
  children: React.ReactNode;
  browseMenuRoots: BrowseMenuRootSummary[] | null;
}

export default function AppShell({ children, browseMenuRoots }: AppShellProps) {
  React.useEffect(() => {
    const cleanup = () => {
      void clearRetiredAnalyticsStorage();
    };
    cleanup();
    window.addEventListener("pageshow", cleanup);
    return () => window.removeEventListener("pageshow", cleanup);
  }, []);
  return (
    <>
      <AuthProvider>
        <OnboardingModalProvider>
          <SignUpPromptProvider>
            <ChatProvider>
              <PreferencesProvider>
                <SearchProvider>
                  <CartProvider>
                    <ShoppingCartProvider>
                      <CollectionsProvider>
                        <SavedBrandsProvider>
                          <SavedSearchesProvider>
                            <SavedProductsProvider>
                              <DebugProvider>
                                <LayoutContent browseMenuRoots={browseMenuRoots}>
                                  {children}
                                </LayoutContent>
                              </DebugProvider>
                            </SavedProductsProvider>
                          </SavedSearchesProvider>
                        </SavedBrandsProvider>
                      </CollectionsProvider>
                    </ShoppingCartProvider>
                  </CartProvider>
                </SearchProvider>
              </PreferencesProvider>
            </ChatProvider>
          </SignUpPromptProvider>
        </OnboardingModalProvider>
      </AuthProvider>
    </>
  );
}
